import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { UI_CONFIG_SCHEMA_VERSION } from '../../shared/models/ui-config.schema';

/**
 * The subset of Monaco's JSON language API this service touches. Reachable
 * only via `window.monaco` once the AMD loader resolves — the bundled
 * TypeScript types mark `languages.json` as `{ deprecated: true }`, which
 * hides `jsonDefaults` even though it is the real (and only) runtime API,
 * so callers re-type it loosely with this interface. See `app.config.ts`'s
 * `onMonacoLoad`, the only place that can produce one of these.
 */
export interface MonacoJsonLanguages {
  json: {
    jsonDefaults: {
      setDiagnosticsOptions(options: {
        validate: boolean;
        allowComments?: boolean;
        trailingCommas?: 'error' | 'warning' | 'ignore';
        schemas?: { uri: string; fileMatch?: string[]; schema: unknown }[];
      }): void;
    };
  };
}

interface FetchedUiConfigSchema {
  $id?: string;
  version?: string;
  [key: string]: unknown;
}

/**
 * True only for a JSON Schema document shaped like the canonical UIConfig
 * schema — not merely "any non-null, non-array object" (PR review
 * follow-up): a 2xx response with an unexpected body (`{}`, `{ error: "..."
 * }`) would otherwise pass through as an effectively empty/permissive
 * Monaco validator while this service still reports success. Checks the
 * handful of top-level members every real document of this kind carries
 * (`$schema`, `type: 'object'`, `properties`) — not `$id`, which
 * {@link MonacoUiConfigSchemaService.applySchema} already treats as
 * legitimately optional, falling back to an in-memory URI.
 */
function isSchemaShaped(value: unknown): value is FetchedUiConfigSchema {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['$schema'] === 'string' &&
    v['type'] === 'object' &&
    typeof v['properties'] === 'object' &&
    v['properties'] !== null &&
    !Array.isArray(v['properties'])
  );
}

/**
 * Fetches the canonical UIConfig JSON Schema from the API and registers it
 * with Monaco's JSON language defaults, so the admin Configs editor's JSON
 * tab gets inline validation hints.
 *
 * This replaces a static `assets/schemas/ui-config.schema.json`
 * fetch of a committed copy that could — and did — silently drift
 * from what the API actually validates for five weeks, because a
 * version-string check couldn't see content-only drift. The API is the only
 * authoritative validator (schema + self-ref + tenant-scoped external-ref
 * checks on every draft save and publish), so serving the same document it
 * validates against removes that drift risk instead of adding another guard
 * for it.
 */
@Injectable({ providedIn: 'root' })
export class MonacoUiConfigSchemaService {
  private readonly http = inject(HttpClient);
  private readonly messages = inject(MessageService);

  /**
   * `onMonacoLoad` is expected to fire once per page load (`ngx-monaco-editor-v2`
   * gates its own AMD load behind a module-level flag), but that guarantee is an
   * internal detail of a third-party library, not a contract this service can
   * rely on. This flag makes `register()` idempotent on its own terms, so a
   * future library change or a second Monaco instance can't cause a duplicate
   * fetch + redundant `setDiagnosticsOptions` call.
   *
   * Set eagerly (before the fetch even starts) so a second `register()` call
   * while the first request is still in flight can't start a duplicate one —
   * but reset back to `false` on every failure path (PR review follow-up):
   * a transient fetch error or a malformed response must not permanently
   * disable every later retry (a Monaco reload, a second editor instance)
   * for the rest of the session. It stays `true` only once a schema has
   * actually been applied.
   */
  private registered = false;

  /**
   * Fetch the schema and register it with Monaco. Failure is surfaced via a
   * toast, never thrown — a schema-fetch outage must not block the editor
   * from opening, only cost it inline hints for that session (the same
   * graceful-degradation contract the previous static-asset fetch honored).
   */
  register(monacoNs: MonacoJsonLanguages): void {
    if (this.registered) {
      return;
    }
    this.registered = true;
    const apiBase = environment.api.replace(/\/+$/, '');
    this.http.get<FetchedUiConfigSchema>(`${apiBase}/ui-config/schema`).subscribe({
      next: (schema) => this.applySchema(monacoNs, schema),
      error: (err: unknown) => {
        this.registered = false;
        console.error('[ui-config] Failed to fetch the UIConfig JSON Schema from the API.', err);
        this.notifySchemaUnavailable();
      },
    });
  }

  private applySchema(monacoNs: MonacoJsonLanguages, schema: unknown): void {
    // A 2xx response can still deliver a null body (e.g. an empty or 204
    // response) or a JSON value that isn't schema-shaped (an array, a string,
    // a number) — none of those are HttpClient errors, so they never reach
    // the `error:` callback above. Treat them the same as a fetch failure
    // instead of handing Monaco a value that would either throw on `.version`
    // (null) or silently register as a permissive, always-valid schema
    // (an array/primitive), with no signal either way.
    if (!isSchemaShaped(schema)) {
      this.registered = false;
      console.error(
        '[ui-config] GET /ui-config/schema returned a non-schema-shaped response; ' +
          'skipping Monaco registration.',
        schema
      );
      this.notifySchemaUnavailable();
      return;
    }
    this.warnOnVersionSkew(schema.version);
    monacoNs.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      trailingCommas: 'error',
      schemas: [
        {
          uri: schema.$id ?? 'inmemory://schema/ui-config.json',
          // The admin configs page is the only Monaco editor in the app
          // today, so we match every JSON model. If we add a second JSON
          // editor with a different schema, give each its own model URI
          // and scope the fileMatch.
          fileMatch: ['*'],
          schema,
        },
      ],
    });
  }

  /**
   * The API's schema can now run ahead of this build's generated types
   * — Monaco could accept a field the deployed UI silently ignores
   * everywhere else. A console warning turns that into a visible signal
   * instead of a silent no-op; it is diagnostic only and never blocks the
   * editor.
   */
  private warnOnVersionSkew(servedVersion: string | undefined): void {
    // `=== undefined` (not a falsy check) so a served-but-empty version string
    // still compares as a mismatch instead of being treated as "not served".
    if (servedVersion === undefined || servedVersion === UI_CONFIG_SCHEMA_VERSION) {
      return;
    }
    // Deliberate developer-facing console signal, not a toast — diagnostic only.
    console.warn(
      `UIConfig schema version mismatch: the API is serving ${servedVersion}, but this build's ` +
        `generated types were compiled against ${UI_CONFIG_SCHEMA_VERSION}. A field Monaco now ` +
        `accepts may silently do nothing until this app is rebuilt and redeployed.`
    );
  }

  private notifySchemaUnavailable(): void {
    this.messages.add({
      severity: 'warn',
      summary: 'Config schema unavailable',
      detail:
        'Inline validation hints are disabled for this session. Your edits are still validated on save.',
    });
  }
}
